import { createContext, useContext, type ComponentType } from 'react';
import { INSIGHT_AGENT_SUB_TYPE, INSIGHT_DASHBOARD_ID_ATTR, INSIGHT_DASHBOARD_SUB_TYPE } from '@circuitry-ai/doc-data';

/**
 * Which runtime answers an analyst, and the app's links into AI Insight.
 *
 * Phase 4 of the AI Analytics Merge Plan moves analysts from the NL analyst (cty-agentic-analyst)
 * to the Insight agent runtime (cty-analytics-agents) one at a time. The switch is two attributes
 * on a decision analyst's aiworker, written with DOC's ordinary aiworker PATCH (an upsert per
 * attribute):
 *
 * - `runtime`: `insight` sends its questions to the Insight runtime; absent or `nl_analyst` keeps
 *   `/api/v1/decision/ask`.
 * - `insight_agent_id`: the Insight agent that answers for it there. The runtime only runs
 *   aiworkers with sub_type `insight_agent`, so this is a linked Insight agent whose configuration
 *   attaches the analyst's skills as `skill:<name>`.
 */
export const ANALYST_RUNTIME_ATTR = 'runtime';
export const INSIGHT_AGENT_ID_ATTR = 'insight_agent_id';
export const ANALYST_RUNTIMES = { NL_ANALYST: 'nl_analyst', INSIGHT: 'insight' } as const;
export const DECISION_ANALYST_TYPE = 'decision';

/** What the routing reads of an aiworker: the list, the by-id GET and router state all fit. */
export interface AnalystWorkerLike {
  readonly aiworker_id?: string;
  readonly name?: string;
  readonly code?: string | null;
  readonly category?: string | null;
  readonly sub_type?: string | null;
  readonly aiworkers_attrs?: ReadonlyArray<{ readonly attr_name?: string; readonly attr_value?: unknown }> | null;
}

/** An attribute's value as trimmed text; '' when it is missing. */
export function workerAttr(worker: AnalystWorkerLike | null | undefined, name: string): string {
  const attr = worker?.aiworkers_attrs?.find((item) => item?.attr_name === name);
  const value = attr?.attr_value;
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

export type AnalystRoute =
  | { readonly runtime: typeof ANALYST_RUNTIMES.NL_ANALYST }
  | { readonly runtime: typeof ANALYST_RUNTIMES.INSIGHT; readonly agentId: string };

const NL_ANALYST_ROUTE: AnalystRoute = { runtime: ANALYST_RUNTIMES.NL_ANALYST };

/**
 * Where an analyst's questions go. Only a decision analyst switched to `insight` with a linked
 * agent goes to the Insight runtime; everything else, including a half-done switch, stays on the
 * NL analyst.
 */
export function analystRoute(worker: AnalystWorkerLike | null | undefined): AnalystRoute {
  if (workerAttr(worker, 'analyst_type').toLowerCase() !== DECISION_ANALYST_TYPE) return NL_ANALYST_ROUTE;
  if (workerAttr(worker, ANALYST_RUNTIME_ATTR).toLowerCase() !== ANALYST_RUNTIMES.INSIGHT) return NL_ANALYST_ROUTE;
  const agentId = workerAttr(worker, INSIGHT_AGENT_ID_ATTR);
  return agentId ? { runtime: ANALYST_RUNTIMES.INSIGHT, agentId } : NL_ANALYST_ROUTE;
}

export const isInsightAgentWorker = (worker: AnalystWorkerLike | null | undefined): boolean =>
  String(worker?.sub_type ?? '').toLowerCase() === INSIGHT_AGENT_SUB_TYPE;

/** A saved AI Insight dashboard shared with the tenant, listed as an analyst; its `dashboard_id` names it. */
export const isInsightDashboardWorker = (worker: AnalystWorkerLike | null | undefined): boolean =>
  String(worker?.sub_type ?? '').toLowerCase() === INSIGHT_DASHBOARD_SUB_TYPE;

export const listedDashboardId = (worker: AnalystWorkerLike | null | undefined): string =>
  (isInsightDashboardWorker(worker) ? workerAttr(worker, INSIGHT_DASHBOARD_ID_ATTR) : '');

/** Insight agents linked from analysts: they answer for those analysts and are listed as them. */
export function linkedInsightAgentIds(workers: ReadonlyArray<AnalystWorkerLike>): Set<string> {
  const ids = new Set<string>();
  for (const worker of workers) {
    if (isInsightAgentWorker(worker)) continue;
    const id = workerAttr(worker, INSIGHT_AGENT_ID_ATTR);
    if (id) ids.add(id);
  }
  return ids;
}

export interface AnalystRuntimeSwitchProps {
  readonly analyst: AnalystWorkerLike;
  /** Called after the switch wrote the analyst's attributes, to reload it. */
  readonly onChanged: () => void;
}

/**
 * The app's AI Insight, as the Analysts page needs it. Provided only where the Insight runtime is
 * configured (dev builds with CTY_INSIGHT_AGENT_URL); without it the page is what it was before,
 * and a switched analyst keeps answering on the NL analyst.
 */
export interface AnalystInsight {
  /** The Insight conversation, asking this agent; without one, the tenant's default Insight Agent. */
  readonly conversationUrl: (agentId?: string) => string;
  readonly editorUrl: (agentId: string) => string;
  /** The editor creating the tenant's default Insight Agent, which runs on built-in settings until then. */
  readonly customiseDefaultUrl: string;
  /** The aiworker code of the tenant's default Insight Agent. */
  readonly defaultAgentCode: string;
  readonly defaultAgentName: string;
  readonly defaultAgentDescription: string;
  /** The admin control on a decision analyst's Configuration tab. */
  readonly RuntimeSwitch?: ComponentType<AnalystRuntimeSwitchProps>;
  /** A saved dashboard's page, where it refreshes with the viewer's own data access. Without it, dashboards are not listed. */
  readonly dashboardUrl?: (dashboardId: string) => string;
  /** Takes a dashboard off All Analysts: it becomes private again (its owner keeps it) and its analyst goes. */
  readonly unpublishDashboard?: (dashboardId: string) => Promise<void>;
}

export const AnalystInsightContext = createContext<AnalystInsight | null>(null);

export const useAnalystInsight = (): AnalystInsight | null => useContext(AnalystInsightContext);
